import { useState, useCallback, useMemo } from "react";
import axiosInstance from "../services/axiosInstance";
import { API_ENDPOINTS } from "../services/api/apiConfig";
import { useUserProfile } from "./useUserProfile";
import { useQuery, useQueryClient } from "@tanstack/react-query";

type SortableField = "recordedAt" | "createdAt" | "updatedAt" | "id";

interface FetchConstanteOptions {
  todayOnly?: boolean;
  showConsulted?: boolean;
  search?: string;
  status?: string;
  page?: number;
  size?: number;
  sortOrder?: "asc" | "desc";
  sortBy?: SortableField;
}

interface PaginationState {
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  consultedCount: number;
  pendingCount: number;
}

interface ResolvedFilters {
  todayOnly: boolean;
  showConsulted: boolean;
  search: string;
  status?: string;
  page: number;
  size: number;
  sortOrder: "asc" | "desc";
  sortBy: SortableField;
}

const DEFAULT_FILTERS: ResolvedFilters = {
  todayOnly: true,
  showConsulted: false,
  search: "",
  status: undefined,
  page: 1,
  size: 10,
  sortOrder: "desc",
  sortBy: "recordedAt",
};

export const useConstanteList = (role: "admin" | "doctor" | "secretary") => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ResolvedFilters>(DEFAULT_FILTERS);
  const { userData, loading: userLoading, error: userError } = useUserProfile();

  const buildEndpoint = useCallback(
    (resolvedFilters: ResolvedFilters) => {
      let baseEndpoint = "";
      if (role === "admin" || role === "secretary") {
        baseEndpoint = API_ENDPOINTS.CONSTANTES.LIST;
      } else {
        if (!userData?.id) {
          return null;
        }
        baseEndpoint = `${API_ENDPOINTS.CONSTANTES.LIST}/doctor/${userData.id}`;
      }

      const params = new URLSearchParams({
        todayOnly: String(resolvedFilters.todayOnly),
        showConsulted: String(resolvedFilters.showConsulted),
        page: String(resolvedFilters.page - 1), // API might be 0-indexed
        size: String(resolvedFilters.size),
        sortDirection: resolvedFilters.sortOrder,
        sortBy: resolvedFilters.sortBy,
      });

      if (resolvedFilters.search) {
        params.append("search", resolvedFilters.search.trim());
      }

      if (resolvedFilters.status) {
        params.append("status", resolvedFilters.status);
      }

      return `${baseEndpoint}?${params.toString()}`;
    },
    [role, userData?.id]
  );

  const { data, isLoading: queryLoading, error: queryError, refetch } = useQuery({
    queryKey: ['constantes', role, userData?.id, filters],
    queryFn: async () => {
      const endpoint = buildEndpoint(filters);
      if (!endpoint) throw new Error("Endpoint cannot be built");

      const response = await axiosInstance.get(endpoint);
      return response.data;
    },
    enabled: !userLoading && !!userData,
  });

  const constantes = useMemo(() => data?.items || [], [data]);

  const pagination = useMemo((): PaginationState => ({
    page: (data?.page ?? 0) + 1,
    size: data?.size ?? filters.size,
    totalItems: data?.totalItems ?? 0,
    totalPages: data?.totalPages ?? 0,
    consultedCount: data?.metadata?.consultedCount ?? 0,
    pendingCount: data?.metadata?.pendingCount ?? 0,
  }), [data, filters.size]);

  const updateFilters = useCallback((newFilters?: FetchConstanteOptions) => {
    if (!newFilters) {
      refetch();
      return;
    }
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      // Reset to page 1 if search or status changes
      page: (newFilters.search !== undefined || newFilters.status !== undefined) ? 1 : (newFilters.page ?? prev.page)
    }));
  }, [refetch]);

  const error = userError || (queryError ? (queryError as any).message : null);

  const setConstantesInCache = useCallback((updateFn: (prev: any[]) => any[]) => {
    queryClient.setQueryData(['constantes', role, userData?.id, filters], (old: any) => {
      if (!old) return old;
      return {
        ...old,
        items: updateFn(old.items || [])
      };
    });
  }, [queryClient, role, userData?.id, filters]);

  return {
    constantes,
    setConstantes: setConstantesInCache,
    pagination,
    filters,
    loading: userLoading || queryLoading,
    error,
    userData,
    refetch: updateFilters, // Re-mapped for compatibility or direct filter updates
    actualRefetch: refetch, // Real refetch for manual triggers
  };
};
